import React from 'react';
import { Camera, ChevronRight, FileImage, Link2, RotateCw, ShieldCheck, UserPlus, X, Check, XCircle } from 'lucide-react';
import { useT } from '../../../i18n/admin';

/**
 * SAM-944 / SAM-1171 — New pass at 375 px, steps 4 to 7 (canvas v256: Main guest x1365/x1820, Contact x2275,
 * Review and pay x2730, Pay with card x3185/x3640, Payment link x4095, Pass created x4550).
 * Presentational only: DocumentScanForm and AccessPage keep the state and the rules and pass it in.
 */

const accent = 'var(--tenant-primary,#005488)';
const fieldBox = 'flex h-[45px] items-center rounded-lg px-3.5 text-base';

export const PhoneTitle: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className="flex flex-col gap-1 px-4 pt-4">
    <div className="text-2xl font-bold text-[var(--ink-900)]">{title}</div>
    {hint && <div className="text-[15px] text-[var(--ink-500)]">{hint}</div>}
  </div>
);

const ActionRow: React.FC<{ icon: React.ReactNode; title: string; hint: string; onClick: () => void }> = ({ icon, title, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="mx-4 flex w-[calc(100%-2rem)] cursor-pointer items-center gap-3.5 border-0 border-b border-solid border-[#E5E5E5] bg-transparent px-0 py-3.5 text-left"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#EDEDED] text-black">{icon}</span>
    <span className="min-w-0 flex-1">
      <span className="block text-lg font-bold leading-tight text-[var(--ink-900)]">{title}</span>
      <span className="mt-0.5 block text-[15px] text-[#6B7280]">{hint}</span>
    </span>
    <ChevronRight size={16} className="text-[#6B7280]" aria-hidden />
  </button>
);

/** Step 4 — Main guest: add the ID photo, or pick a known guest, or send the registration link. */
export const PhoneGuestStep: React.FC<{
  title: string;
  onPhoto: (file: File) => void;
  onContacts?: () => void;
  onShareLink?: () => void;
  onManual?: () => void;
  error?: string;
  analyzing?: boolean;
}> = ({ title, onPhoto, onContacts, onShareLink, onManual, error, analyzing }) => {
  const { t } = useT();
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <div>
      <PhoneTitle title={title} />
      <div className="mx-4 mt-4 flex flex-col items-center gap-4 rounded-xl border-[1.5px] border-dashed border-[#D1D5DB] bg-[#F9FAFB] py-[30px]">
        <FileImage size={36} strokeWidth={1.6} className="text-[#6B7280]" aria-hidden />
        <button
          type="button"
          disabled={analyzing}
          onClick={() => input.current?.click()}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border-0 bg-[color-mix(in_srgb,var(--tenant-primary,#005488)_10%,transparent)] px-5 py-2 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50"
          style={{ color: accent }}
        >
          <Camera size={14} aria-hidden />
          {analyzing ? t('booking.phone.reading') : t('booking.phone.addIdPhoto')}
        </button>
        <input
          ref={input}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          capture="environment"
          className="hidden"
          aria-label={t('booking.phone.addIdPhoto')}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onPhoto(file);
          }}
        />
      </div>
      {error && <div role="alert" className="mx-4 mt-2 text-sm text-[var(--bad-fg,#b42318)]">{error}</div>}
      {onManual && (
        <button type="button" onClick={onManual} className="mx-4 mt-3 cursor-pointer border-0 bg-transparent p-0 text-sm font-bold underline" style={{ color: accent }}>
          {t('booking.phone.enterManually')}
        </button>
      )}
      {(onContacts || onShareLink) && (
        <>
          <div className="mx-4 mb-1 mt-[18px] flex items-center gap-3">
            <span className="h-px flex-1 bg-[#E5E5E5]" />
            <span className="text-[13px] font-bold text-[#6B7280]">{t('booking.phone.or')}</span>
            <span className="h-px flex-1 bg-[#E5E5E5]" />
          </div>
          {onContacts && <ActionRow icon={<UserPlus size={19} aria-hidden />} title={t('booking.phone.fromContacts')} hint={t('booking.phone.fromContactsHint')} onClick={onContacts} />}
          {onShareLink && <ActionRow icon={<Link2 size={19} aria-hidden />} title={t('booking.phone.shareRegistration')} hint={t('booking.phone.shareRegistrationHint')} onClick={onShareLink} />}
        </>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-base font-bold text-[var(--ink-900)]">{label}</span>
    {children}
  </div>
);

const ReadOnly: React.FC<{ value: string; placeholder?: string }> = ({ value, placeholder }) => (
  <span className={`${fieldBox} ${value ? 'bg-[#E8E8E8] text-black' : 'border border-solid border-[#E8E8E8] bg-white text-[#8E8E93]'}`}>{value || placeholder}</span>
);

const inputCls = `${fieldBox} w-full border-0 bg-[#E8E8E8] text-black outline-none placeholder:text-[#8E8E93]`;

/** Step 4b — the ID was scanned: preview with Rescan and what was read from it. */
export const PhoneScannedDetails: React.FC<{
  title: string;
  preview?: string;
  onRescan: (file: File) => void;
  rows: Array<{ label: string; value: string }>;
}> = ({ title, preview, onRescan, rows }) => {
  const { t } = useT();
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <div>
      <PhoneTitle title={title} />
      <div className="flex flex-col gap-3.5 p-4">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold tracking-[1.2px] text-[var(--ink-500)]">{t('booking.phone.scannedDocument')}</span>
          <div className="relative h-[180px] overflow-hidden rounded-xl border border-solid border-[#E8E8E8] bg-[#DDE3E8]">
            {preview && <img src={preview} alt={t('booking.phone.scannedDocument')} className="h-full w-full object-cover" />}
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="absolute right-2 top-2 flex cursor-pointer items-center gap-1 rounded-full border-0 bg-black/55 px-2.5 py-1.5 text-xs font-bold text-white"
            >
              <RotateCw size={11} strokeWidth={2.4} aria-hidden />
              {t('booking.phone.rescan')}
            </button>
            <input
              ref={input}
              type="file"
              accept=".png,.jpg,.jpeg,image/png,image/jpeg"
              capture="environment"
              className="hidden"
              aria-label={t('booking.phone.rescan')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onRescan(file);
              }}
            />
          </div>
        </div>
        {rows.map((r) => (
          <Field key={r.label} label={r.label}><ReadOnly value={r.value} placeholder={t('booking.phone.optional')} /></Field>
        ))}
        <div className="text-[13px] text-[var(--ink-500)]">{t('booking.phone.scanNote')}</div>
      </div>
    </div>
  );
};

/** Step 4b without a photo (registration link, or typed): the two names. */
export const PhoneTypedDetails: React.FC<{
  title: string;
  firstName: string;
  lastName: string;
  onFirstName: (v: string) => void;
  onLastName: (v: string) => void;
  hint?: string;
}> = ({ title, firstName, lastName, onFirstName, onLastName, hint }) => {
  const { t } = useT();
  return (
    <div>
      <PhoneTitle title={title} hint={hint} />
      <div className="flex flex-col gap-3.5 p-4">
        <Field label={t('booking.phone.firstName')}>
          <input className={inputCls} value={firstName} autoComplete="given-name" placeholder={t('booking.phone.firstName')} onChange={(e) => onFirstName(e.target.value)} />
        </Field>
        <Field label={t('booking.phone.lastName')}>
          <input className={inputCls} value={lastName} autoComplete="family-name" placeholder={t('booking.phone.lastName')} onChange={(e) => onLastName(e.target.value)} />
        </Field>
      </div>
    </div>
  );
};

/** Step 5 — How do we reach them? The phone input is the app's FormattedPhoneInput, passed in. */
export const PhoneContactStep: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-6">
      <PhoneTitle title={t('booking.phone.reachTitle')} hint={t('booking.phone.reachHint')} />
      <div className="flex flex-col gap-3.5 px-4 pb-4">
        <Field label={t('booking.phone.phoneNumber')}>{children}</Field>
      </div>
    </div>
  );
};

/** Card brand from the number's prefix. Visa, Mastercard and American Express are the accepted ones. */
export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'other' | '';
export const cardBrand = (digits: string): CardBrand => {
  if (!digits) return '';
  if (/^4/.test(digits)) return 'visa';
  if (/^(5[1-5]|2(2[2-9]|[3-6]\d|7[01]|720))/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^(6011|65|64[4-9])/.test(digits)) return 'discover';
  return digits.length >= 2 ? 'other' : '';
};
export const brandAccepted = (b: CardBrand): boolean => b === '' || b === 'visa' || b === 'mastercard' || b === 'amex';
const BRAND_LABEL: Record<CardBrand, string> = { '': '', visa: 'VISA', mastercard: 'MASTERCARD', amex: 'AMEX', discover: 'DISCOVER', other: '' };

export interface CardValue { cardNumber: string; cardHolder: string; expiryDate: string; cvv: string }

export const cardIsValid = (c?: CardValue): boolean => {
  if (!c) return false;
  if (c.cardNumber.length !== 16 || !brandAccepted(cardBrand(c.cardNumber))) return false;
  if (c.cardHolder.trim().length < 3) return false;
  const m = c.expiryDate.match(/^(\d{2})\/(\d{2})$/);
  if (!m) return false;
  const month = Number(m[1]);
  const year = Number(m[2]);
  const now = new Date();
  if (month < 1 || month > 12 || year < now.getFullYear() % 100 || (year === now.getFullYear() % 100 && month < now.getMonth() + 1)) return false;
  return /^\d{3,4}$/.test(c.cvv);
};

const groupDigits = (d: string) => (d.match(/.{1,4}/g) ?? []).join(' ');

/** All but the last four digits as bullets (SAM-1281): the number is shown in full only while it is being typed. */
export const maskCardDigits = (shown: string): string =>
  shown.replace(/\d/g, (digit, index, all) => (all.slice(index + 1).replace(/\D/g, '').length >= 4 ? '•' : digit));

const CardField: React.FC<{
  label: string; value: string; onChange: (v: string) => void; ok?: boolean; bad?: boolean;
  inputMode?: 'numeric' | 'text'; autoComplete: string; right?: React.ReactNode; placeholder?: string; maxLength?: number;
  /** The card number: complete with the focus, only its last four digits without it. */
  maskBlurred?: boolean;
  /** The CVV: never shown (a password field). */
  secret?: boolean;
}> = ({ label, value, onChange, ok, bad, inputMode, autoComplete, right, placeholder, maxLength, maskBlurred, secret }) => {
  const [focused, setFocused] = React.useState(false);
  return (
  <label className="flex flex-col gap-1">
    <span className="text-[13px] font-bold text-[var(--ink-500)]">{label}</span>
    <span className={`flex items-center gap-2 rounded-lg border border-solid p-3 text-base ${bad ? 'border-[rgba(212,67,51,.7)]' : ok ? 'border-[rgba(14,131,69,.6)]' : 'border-[#E8E8E8]'}`}>
      <input
        value={maskBlurred && !focused ? maskCardDigits(value) : value}
        type={secret ? 'password' : 'text'}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        maxLength={maxLength}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base outline-none"
      />
      {right}
      {bad ? <XCircle size={16} className="text-[#D44333]" aria-hidden /> : ok ? <Check size={16} className="rounded-full bg-[#0E8345] p-0.5 text-white" aria-hidden /> : null}
    </span>
  </label>
  );
};

/** Step 6a / 6b — Pay with card, as a sheet over Review and pay. A brand that is not accepted is an error in the field. */
export const PhoneCardSheet: React.FC<{
  value: CardValue;
  onChange: (v: CardValue) => void;
  payLabel: string;
  onPay: () => void;
  onClose: () => void;
  busy?: boolean;
  error?: string | null;
  /** A service booking may be free: the server decides, so an empty card is allowed there. */
  optional?: boolean;
}> = ({ value, onChange, payLabel, onPay, onClose, busy, error, optional }) => {
  const { t } = useT();
  const brand = cardBrand(value.cardNumber);
  const brandBad = !brandAccepted(brand);
  const numOk = value.cardNumber.length === 16 && !brandBad;
  const expiry = (raw: string) => {
    const d = raw.replace(/\D/g, '').slice(0, 4);
    return d.length >= 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };
  const empty = !value.cardNumber && !value.cardHolder && !value.expiryDate && !value.cvv;
  const valid = cardIsValid(value) || Boolean(optional && empty);
  const set = (patch: Partial<CardValue>) => onChange({ ...value, ...patch });
  return (
    <div className="absolute inset-0 z-30 flex flex-col" role="dialog" aria-modal="true" aria-label={t('booking.phone.payWithCard')}>
      <button type="button" aria-label={t('booking.phone.close')} onClick={onClose} className="h-[72px] shrink-0 cursor-default border-0 bg-black/40" />
      <div className="flex min-h-0 flex-1 flex-col rounded-t-xl bg-white">
        <div className="relative flex h-[52px] shrink-0 items-center border-0 border-b border-solid border-[#E8E8E8]">
          <button type="button" aria-label={t('booking.phone.close')} onClick={onClose} className="absolute left-2 flex h-11 w-11 cursor-pointer items-center justify-center border-0 bg-transparent">
            <X size={18} aria-hidden />
          </button>
          <span className="flex-1 text-center text-[17px] font-semibold">{t('booking.phone.payWithCard')}</span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1">
            <CardField
              label={t('card.number')}
              value={groupDigits(value.cardNumber)}
              onChange={(v) => set({ cardNumber: v.replace(/\D/g, '').slice(0, 16) })}
              ok={numOk}
              bad={brandBad}
              inputMode="numeric"
              autoComplete="cc-number"
              maskBlurred
              right={brand && BRAND_LABEL[brand] ? <span className={`text-[11px] font-extrabold tracking-wide ${brandBad ? 'text-[#D44333]' : 'text-[#1A1F71]'}`}>{BRAND_LABEL[brand]}</span> : null}
            />
            {brandBad && <div role="alert" className="text-[13px] text-[#D44333]">{t('booking.phone.brandNotAccepted', { brand: brand === 'discover' ? 'Discover' : t('booking.phone.thatCard') })}</div>}
          </div>
          <CardField label={t('card.holder')} value={value.cardHolder} onChange={(v) => set({ cardHolder: v.toUpperCase() })} ok={value.cardHolder.trim().length >= 3} autoComplete="cc-name" />
          <div className="flex gap-3">
            <div className="min-w-0 flex-1"><CardField label={t('card.expiry')} value={value.expiryDate} onChange={(v) => set({ expiryDate: expiry(v) })} ok={/^\d{2}\/\d{2}$/.test(value.expiryDate)} inputMode="numeric" autoComplete="cc-exp" maxLength={5} /></div>
            <div className="min-w-0 flex-1"><CardField label={t('card.cvv')} value={value.cvv} onChange={(v) => set({ cvv: v.replace(/\D/g, '').slice(0, 4) })} ok={/^\d{3,4}$/.test(value.cvv)} inputMode="numeric" autoComplete="cc-csc" secret /></div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[var(--ink-500)]">
            <ShieldCheck size={13} aria-hidden />
            {t('booking.phone.cardNote')}
          </div>
          {error && <div role="alert" className="text-sm text-[var(--bad-fg,#b42318)]">{error}</div>}
        </div>
        <div className="px-[22px] py-4">
          <button
            type="button"
            onClick={onPay}
            disabled={!valid || busy}
            className={`h-[57px] w-full rounded-xl border-0 text-lg font-bold ${valid && !busy ? 'cursor-pointer text-white' : 'cursor-not-allowed bg-[#F3F3F3] text-[#8E8E93]'}`}
            style={valid && !busy ? { background: accent } : undefined}
          >
            {payLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Rule = () => <div className="h-px bg-[#E8E8E8]" />;
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="px-4 pb-3 pt-4 text-lg font-bold">{children}</div>;

export interface ReviewData {
  name: string;
  phone: string;
  /** The service being sold; its price is the server's, never shown or computed here. */
  service: string;
  /** SAM-1312 (U36): where the guest goes, as picked in step 1 (estate group · estate, or the business). */
  destination?: string;
  /** How it is paid when the guest pays by link (a pass_fee service, SAM-1171). */
  payment?: string;
  date: string;
  guests: string[];
}

/** Step 6 — Review, in one screen. A pass_fee service is paid by the guest through a payment link (SAM-1171). */
export const PhoneReviewStep: React.FC<{
  data: ReviewData;
  error?: string | null;
}> = ({ data, error }) => {
  const { t } = useT();
  const initials = data.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '·';
  const detail = (label: string, value: React.ReactNode) => (
    <>
      <div className="px-4 py-3">
        <div className="text-xs text-[var(--ink-500)]">{label}</div>
        <div className="mt-1 text-base font-bold">{value}</div>
      </div>
      <Rule />
    </>
  );
  return (
    <div>
      <SectionTitle>{t('booking.phone.mainGuest')}</SectionTitle>
      <div className="flex items-center gap-3 px-4 pb-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--tenant-primary,#005488)_10%,transparent)] text-[17px] font-bold" style={{ color: accent }}>{initials}</span>
        <div className="flex-1">
          <div className="text-[17px] font-bold">{data.name}</div>
          <div className="mt-0.5 text-sm text-[var(--ink-500)]">{data.phone}</div>
        </div>
      </div>
      <Rule />
      <SectionTitle>{t('booking.phone.bookingDetails')}</SectionTitle>
      {data.destination && detail(t('passConfirm.destination'), data.destination)}
      {data.service && detail(t('booking.access.service'), data.service)}
      {data.payment && detail(t('passConfirm.payment'), data.payment)}
      {detail(t('booking.phone.date'), data.date)}
      {data.guests.length > 0 && (
        <>
          <div className="px-4 py-3">
            <div className="text-xs text-[var(--ink-500)]">{t('booking.phone.guests')}</div>
            <div className="mt-2 flex gap-2">
              {data.guests.map((g) => <span key={g} className="rounded-xl bg-[#E8E8E8] px-3 py-1 text-xs font-bold">{g}</span>)}
            </div>
          </div>
          <Rule />
        </>
      )}
      {error && <div role="alert" className="mx-4 mb-2 text-sm text-[var(--bad-fg,#b42318)]">{error}</div>}
    </div>
  );
};

/** Step 7 — Pass created: who, status, what, and Share pass + Done. */
export const PhonePassCreated: React.FC<{
  name: string;
  phone: string;
  status: string;
  /** Colours of the status chip (SAM-1320): Pending is amber like the list, not always the green of Confirmed. */
  statusColors?: { bg: string; color: string };
  rows: Array<{ label: string; value: string }>;
  /** The payment link of a pass paid by link (SAM-1171); it replaces the «sent by SMS» line, the SMS goes out when it is paid. */
  linkPanel?: React.ReactNode;
  onShare: () => void;
  onDone: () => void;
}> = ({ name, phone, status, statusColors, rows, linkPanel, onShare, onDone }) => {
  const { t } = useT();
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '·';
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex items-center gap-4 px-4 py-6">
          <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full text-2xl font-bold text-white" style={{ background: accent }}>{initials}</span>
          <div className="flex flex-col gap-1">
            <span className="text-xl font-bold">{name}</span>
            <span className="text-sm" style={{ color: accent }}>{phone}</span>
            <span
              className={`w-fit rounded-full px-2.5 py-[3px] text-xs font-bold ${statusColors ? '' : 'bg-[rgba(14,131,69,.12)] text-[#0E8345]'}`}
              style={statusColors ? { background: statusColors.bg, color: statusColors.color } : undefined}
            >{status}</span>
          </div>
        </div>
        {!linkPanel && <div className="px-6 pb-3 text-center text-[13px] text-[var(--ink-500)]">{t('booking.phone.sentBySms')}</div>}
        {rows.map((r) => (
          <React.Fragment key={r.label}>
            <div className="mx-4 h-px bg-[#E8E8E8]" />
            <div className="px-4 py-3.5">
              <div className="text-sm">{r.label}</div>
              <div className="mt-1 text-sm text-[var(--ink-500)]">{r.value}</div>
            </div>
          </React.Fragment>
        ))}
        {linkPanel && <div className="px-4 pb-3">{linkPanel}</div>}
      </div>
      <div className="flex shrink-0 flex-col gap-3 bg-white px-[22px] py-4">
        <button type="button" onClick={onShare} className="h-[52px] w-full cursor-pointer rounded-xl border border-solid bg-white text-base font-bold" style={{ color: accent, borderColor: accent }}>{t('booking.phone.sharePass')}</button>
        <button type="button" onClick={onDone} className="h-[57px] w-full cursor-pointer rounded-xl border-0 text-lg font-bold text-white" style={{ background: accent }}>{t('booking.phone.done')}</button>
      </div>
    </div>
  );
};
